import React from 'react';
import { Button, Chip, Modal, ModalBody, ModalContent, ModalHeader } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import { formatCoverageDate, formatCurrency } from '../utils/dateutils';
import { formatKeyForDisplay } from '../utils/commonutils';
import { formatSectionTitle, getFieldFormat } from './actionOutputV2Utils';
import { MarkdownContent } from './AISummaryAccordion';
import { renderClaimScoreFormattedField } from './claimScoreRendererShared';

export type SourceJsonModalState = { title: string; data: unknown };

/** Catalog mapping used to filter C&K rows by the row source clicked. */
const CK_SOURCE_TO_CATALOGS: Record<string, string[]> = {
    'c&k_oem_part': ['OEMPart', 'OEMAftermarket'],
    'c&k_after_market': ['OEMAftermarket', 'OEMPart'],
    'c&k_remanpowertrain': ['RemanPowertrain', 'UsedPowertrain'],
    'c&k_usedpowertrain': ['UsedPowertrain', 'RemanPowertrain'],
};

export function filterCkDataForSource(ckSourceData: any[], sourceRaw: string): any[] {
    if (!Array.isArray(ckSourceData) || ckSourceData.length === 0) return ckSourceData;
    const catalogs = CK_SOURCE_TO_CATALOGS[sourceRaw.toLowerCase()];
    if (!catalogs) return ckSourceData;
    const filtered = ckSourceData.filter((item: any) => catalogs.includes(item?.partCatalog));
    return filtered.length > 0 ? filtered : ckSourceData;
}

const buildForteCatalogItem = (
    forte: Record<string, unknown>,
    pricing: { customerPrice: number | null; listPrice: number | null; totalPrice: number | null },
    resourceUrls: { url: string; type: string; description: string }[]
) => ({
    description: forte.description ?? '—',
    quotedPartNumber: forte.partNumber ?? '—',
    partRequested: { partNumber: forte.partNumber ?? '', searchType: 'oempartnumber' },
    ...pricing,
    coreValue: null,
    stockStatus: null,
    deliveryDate: null,
    resourceUrls,
    warranties: [] as unknown[],
    shippingCosts: [] as unknown[],
    _forteLastPartUpdate: forte.lastPartUpdate ?? null,
});

export function normalizeForteToPartItems(forte: any): any[] {
    if (!forte || typeof forte !== 'object') return [];
    const isNapa = String(forte.napa).toLowerCase() === 'true';
    const retail = forte.retail == null ? null : Number(forte.retail);
    const graphicUrls = forte.graphic ? [{ url: forte.graphic, type: 'image', description: '' }] : [];
    const baseItem = buildForteCatalogItem(
        forte,
        {
            customerPrice: retail,
            listPrice: retail,
            totalPrice: forte.totalPrice == null ? null : Number(forte.totalPrice),
        },
        graphicUrls
    );
    const dashItem = buildForteCatalogItem(
        forte,
        { customerPrice: null, listPrice: null, totalPrice: null },
        []
    );
    if (isNapa) {
        return [
            { ...dashItem, partCatalog: 'OEMPart' },
            { ...baseItem, partCatalog: 'OEMAftermarket' },
        ];
    }
    return [
        { ...baseItem, partCatalog: 'OEMPart' },
        { ...dashItem, partCatalog: 'OEMAftermarket' },
    ];
}

export function normalizeTealToPartItems(teal: any): any[] {
    if (!teal || typeof teal !== 'object') return [];
    let parts: any[] = [];
    if (Array.isArray(teal.partsFound)) {
        parts = teal.partsFound;
    } else if (Array.isArray(teal.partsFoundprice)) {
        parts = teal.partsFoundprice;
    }
    if (parts.length === 0) return [];
    const catalogToCk = (pc: string): 'OEMPart' | 'OEMAftermarket' | null => {
        if (!pc || typeof pc !== 'string') return null;
        const lower = pc.toLowerCase();
        if (lower.includes('oem')) return 'OEMPart';
        if (lower.includes('aftermarket')) return 'OEMAftermarket';
        return null;
    };
    const mappedParts = parts.map((p: any) => {
        const partCatalog = catalogToCk(p?.partCatalog);
        if (partCatalog == null) return null;
        let warranties: any[] = [];
        if (p?.warranty) {
            warranties = [p.warranty];
        } else if (Array.isArray(p?.warranties)) {
            warranties = p.warranties;
        }
        return {
            partCatalog,
            description: p.description ?? '—',
            quotedPartNumber: p.quotedPartNumber ?? p.partRequested?.partNumber ?? '—',
            partRequested: p.partRequested ?? { partNumber: '', searchType: 'oempartnumber' },
            customerPrice: p.customerPrice == null ? null : Number(p.customerPrice),
            listPrice: p.listPrice == null ? null : Number(p.listPrice),
            totalPrice: p.totalPrice == null ? null : Number(p.totalPrice),
            coreValue: p.coreValue == null ? null : Number(p.coreValue),
            stockStatus: p.stockStatus ?? null,
            deliveryDate: p.deliveryDate ?? null,
            resourceUrls: Array.isArray(p.resourceUrls) ? p.resourceUrls : [],
            warranties,
            shippingCosts: [{
                partCatalog,
                shippingCost: p.shippingPrice == null ? null : Number(p.shippingPrice),
                coreShippingCost: (p.coreShipping == null || p.coreShipping === '') ? null : Number(p.coreShipping),
            }],
        };
    });
    return mappedParts.filter((item: any) => item != null);
}

type PartSourceContentProps = {
    data: any[];
    quoteTimestamp?: string;
    sourceType: 'ck' | 'forte' | 'teal';
};

const PartSourceModalContent: React.FC<PartSourceContentProps> = ({ data, quoteTimestamp, sourceType }) => {
    const { t } = useTranslation();
    if (!Array.isArray(data) || data.length === 0) return null;
    const isForte = sourceType === 'forte';
    const ckData = data;
    const first = ckData[0];
    const partNumber = first?.quotedPartNumber ?? first?.partRequested?.partNumber ?? '—';
    const oemItem = ckData.find((item: any) => item?.partCatalog === 'OEMPart');
    const aftermarketItem = ckData.find((item: any) => item?.partCatalog === 'OEMAftermarket');
    const catalogRowsForDisplay = ckData.filter(
        (item: any) => item?.partCatalog != null && typeof item.partCatalog === 'string',
    );
    const getCkCatalogTitle = (partCatalog: string): string => {
        if (partCatalog === 'OEMPart') return 'OEM';
        if (partCatalog === 'OEMAftermarket') return t('Aftermarket');
        if (partCatalog === 'RemanPowertrain') return t('Reman Powertrain');
        return partCatalog.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
    };
    const oemPrice = oemItem?.customerPrice == null ? Infinity : Number(oemItem.customerPrice);
    const aftermarketPrice = aftermarketItem?.customerPrice == null ? Infinity : Number(aftermarketItem.customerPrice);
    const isInStock = (item: any) => {
        if (!item) return false;
        const s = item.stockStatus;
        if (s === true || String(s).toLowerCase() === 'yes') return true;
        return false;
    };
    const oemInStock = isInStock(oemItem);
    const aftermarketInStock = isInStock(aftermarketItem);
    const oemIsPreferred = (() => {
        if (isForte) {
            return oemItem?.customerPrice != null;
        }
        if (!oemItem) return false;
        if (!aftermarketItem) return true;
        if (oemInStock && aftermarketInStock) return oemPrice <= aftermarketPrice;
        if (oemInStock) return true;
        if (aftermarketInStock) return false;
        return oemPrice <= aftermarketPrice;
    })();
    const preferredItem = oemIsPreferred ? oemItem : aftermarketItem;
    const partName = (preferredItem ?? first)?.description ?? '—';
    const preferredChipLabel = oemIsPreferred ? 'OEM' : t('Aftermarket');
    const quoteDateStr = quoteTimestamp ? formatCoverageDate(quoteTimestamp) : '—';
    const isPowertrainCatalog = (pc: string) => pc === 'RemanPowertrain' || pc === 'UsedPowertrain';
    const hasNonPowertrainItems = catalogRowsForDisplay.some((item: any) => !isPowertrainCatalog(item?.partCatalog));
    const deliveryItem = ckData.find((item: any) => item?.deliveryDate) ?? first;
    const deliveryDateStr = deliveryItem?.deliveryDate ? formatCoverageDate(deliveryItem.deliveryDate) : '—';
    const forteLastPartUpdate = first?._forteLastPartUpdate ?? null;
    const formatSavings = (list: any, customer: any) => {
        if (list == null || customer == null || list === '' || customer === '') return '—';
        const l = Number(list);
        const c = Number(customer);
        if (Number.isNaN(l) || Number.isNaN(c)) return '—';
        const diff = l - c;
        return formatCurrency(diff);
    };
    const getWarrantyListForItem = (item: any): { name: string; price: string }[] | null => {
        if (!item) return null;
        let raw: any[] = [];
        if (Array.isArray(item?.warranties) && item.warranties.length > 0) {
            raw = item.warranties;
        } else if (item?.warranty && typeof item.warranty === 'object' && !Array.isArray(item.warranty)) {
            raw = [item.warranty];
        }
        const list = raw
            .filter((w: any) => w?.warrantyDisplayName != null)
            .map((w: any) => ({
                name: w.warrantyDisplayName,
                price: w.warrantyPrice == null ? '—' : formatCurrency(w.warrantyPrice),
            }));
        return list.length > 0 ? list : null;
    };
    const getShippingForItem = (item: any, partCatalog: string) => {
        if (!item) return null;
        const costs = Array.isArray(item?.shippingCosts) ? item.shippingCosts : [];
        return costs.find((s: any) => s?.partCatalog === partCatalog) ?? costs[0] ?? null;
    };
    const looksLikeImageUrl = (url: unknown): boolean => {
        if (typeof url !== 'string' || !url.trim()) return false;
        const s = url.trim();
        const pathPart = s.split('?')[0].toLowerCase();
        if ((s.startsWith('http://') || s.startsWith('https://')) && (pathPart.endsWith('.jpg') || pathPart.endsWith('.jpeg') || pathPart.endsWith('.png') || pathPart.endsWith('.webp'))) return true;
        return false;
    };
    const firstResourceUrl = (() => {
        for (const item of ckData) {
            const list = item?.resourceUrls;
            if (Array.isArray(list) && list.length > 0 && list[0]?.url && looksLikeImageUrl(list[0].url)) {
                return { url: list[0].url, description: list[0].description };
            }
        }
        return null;
    })();
    const stockDisplay = (item: any) => {
        const s = item?.stockStatus;
        if (s == null || s === '' || String(s).toLowerCase() === 'no' || s === false) return t('None available');
        if (String(s).toLowerCase() === 'yes' || s === true) return t('In stock');
        return t('In stock');
    };
    const stockAvailable = (item: any) => {
        const s = item?.stockStatus;
        if (s == null || s === '' || String(s).toLowerCase() === 'no' || s === false) return false;
        return true;
    };

    const renderStockStatus = (item: any) => {
        if (!item) {
            return (
                <>
                    <Icon icon="mdi:alert-circle-outline" className="w-4 h-4 text-amber-500" />
                    <span>{t('None available')}</span>
                </>
            );
        }
        if (stockAvailable(item)) {
            return (
                <>
                    <Icon icon="mdi:check-circle" className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span>{stockDisplay(item)}</span>
                </>
            );
        }
        return (
            <>
                <Icon icon="mdi:alert-circle-outline" className="w-4 h-4 text-amber-500" />
                <span>{stockDisplay(item)}</span>
            </>
        );
    };

    const renderCKWarrantySection = (item: any, keyPrefix: string, sectionClassName = 'mb-4 border-t border-default-200 pt-4') => {
        const warrantyList = getWarrantyListForItem(item);
        return (
            <div className={sectionClassName}>
                <div className="flex items-center gap-2 font-semibold text-default-700 mb-2">
                    <Icon icon="mdi:shield-check-outline" className="w-4 h-4 text-default-500" />
                    {t('Warranty')}
                </div>
                {warrantyList ? (
                    <div className="flex flex-wrap gap-2">
                        {warrantyList.map((w, idx) => (
                            <div key={`${keyPrefix}-w-${w.name}-${idx}`} className="flex justify-between items-center p-1 rounded-lg bg-default-100 border border-default-200 gap-4 min-w-[100px]">
                                <div className="text-xs text-default-500">{w.name}</div>
                                <div className="text-sm font-medium text-default-700">{w.price}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-2xl text-default-400 font-medium">—</div>
                )}
            </div>
        );
    };

    const renderCKShippingSection = (item: any, partCatalog: string, keyPrefix: string, sectionClassName = 'mb-4 border-t border-default-200 pt-4') => {
        const shipping = getShippingForItem(item, partCatalog);
        return (
            <div className={sectionClassName}>
                <div className="flex items-center gap-2 font-semibold text-default-700 mb-2">
                    <Icon icon="mdi:truck-delivery-outline" className="w-4 h-4 text-default-500" />
                    {t('Shipping Cost')}
                </div>
                <div className="flex flex-wrap gap-2">
                    <div className="flex justify-between items-center p-1 rounded-lg bg-default-100 border border-default-200 gap-4 min-w-[100px]">
                        <div className="text-xs text-default-500">{t('Shipping')}</div>
                        <div className="text-sm font-medium text-default-700">{formatCurrency(shipping?.shippingCost)}</div>
                    </div>
                    <div className="flex justify-between items-center p-1 rounded-lg bg-default-100 border border-default-200 gap-4 min-w-[100px]">
                        <div className="text-xs text-default-500">{t('Core Shipping')}</div>
                        <div className="text-sm font-medium text-default-700">{formatCurrency(shipping?.coreShippingCost)}</div>
                    </div>
                </div>
            </div>
        );
    };

    const renderCKPricingRows = (item: any, pricingClassName = 'space-y-2 text-sm mb-4') => (
        <div className={pricingClassName}>
            <div className="flex justify-between"><span className="text-default-500">{t('List price')}</span><span className="font-medium">{formatCurrency(item?.listPrice)}</span></div>
            <div className="flex justify-between"><span className="text-default-500">{t('Your price')}</span><span className="font-medium">{formatCurrency(item?.customerPrice)}</span></div>
            <div className="flex justify-between"><span className="text-default-500">{t('Total price')}</span><span className="font-medium">{formatCurrency(item?.totalPrice)}</span></div>
            <div className="flex justify-between"><span className="text-default-500">{t('Core')}</span><span className="font-medium">{formatCurrency(item?.coreValue)}</span></div>
            <div className="flex justify-between"><span className="text-default-500">{t('Savings')}</span><span className="font-medium text-green-600 dark:text-green-400">{formatSavings(item?.listPrice, item?.customerPrice)}</span></div>
        </div>
    );

    const renderCKMessagesSection = (item: any, keyPrefix: string) => {
        const messages = Array.isArray(item?.messages) ? item.messages : [];
        if (messages.length === 0) return null;
        return (
            <div className="border-t border-default-200 pt-4">
                <div className="flex items-center gap-2 font-semibold text-default-700 mb-2">
                    <Icon icon="mdi:message-text-outline" className="w-4 h-4 text-default-500" />
                    {t('Messages')}
                </div>
                <div className="space-y-2 max-h-[170px] overflow-y-auto pr-1">
                    {messages.map((msg: any, idx: number) => (
                        <div key={`${keyPrefix}-msg-${idx}`} className="rounded-lg bg-default-100 border border-default-200 p-2">
                            {msg?.category && (
                                <div className="text-xs font-semibold text-default-500 uppercase tracking-wide mb-1">
                                    {formatKeyForDisplay(msg.category)}
                                </div>
                            )}
                            {msg?.content && (
                                <div className="text-sm text-default-700 prose prose-sm max-w-none [&_p]:m-0 [&_p]:text-sm">
                                    <MarkdownContent content={msg.content} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderCKOptionCard = (opts: {
        item: any;
        title: string;
        partCatalog: string;
        isPreferred: boolean;
        cardKey: string;
        showAlternativeChipWhenEmpty: boolean;
        cardClassNameExtra?: string;
        showDeliveryDate?: boolean;
    }) => {
        const { item, title, partCatalog, isPreferred, cardKey, showAlternativeChipWhenEmpty, cardClassNameExtra = '', showDeliveryDate } = opts;
        const hasItem = !!item;
        let chip: React.ReactNode = null;
        if (!hasItem && showAlternativeChipWhenEmpty) {
            chip = <Chip size="sm" variant="bordered" color="default" radius="md">{t('Alternative')}</Chip>;
        } else if (hasItem && isPreferred) {
            chip = <Chip size="sm" color="primary" variant="flat" radius="md">{t('Preferred')}</Chip>;
        } else if (hasItem) {
            chip = <Chip size="sm" variant="bordered" color="default" radius="md">{t('Alternative')}</Chip>;
        }
        const cardClassName = hasItem && isPreferred ? 'border-primary/40 bg-primary/5' : 'border-default-200 bg-content2/50';
        const itemDeliveryDate = item?.deliveryDate ? formatCoverageDate(item.deliveryDate) : null;
        return (
            <div className={`rounded-lg border-2 p-4 ${cardClassName} ${cardClassNameExtra}`.trim()}>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                        <span className="font-medium text-default-700">{title}</span>
                        {chip}
                    </div>
                    {showDeliveryDate && itemDeliveryDate && (
                        <span className="inline-flex items-center gap-1 text-xs text-default-500">
                            <Icon icon="mdi:truck-delivery-outline" className="w-3.5 h-3.5" />
                            {t('Delivery By')}: {itemDeliveryDate}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 text-sm text-default-700 mb-4">
                    {renderStockStatus(item)}
                </div>
                {renderCKPricingRows(item)}
                {renderCKWarrantySection(item, cardKey)}
                {renderCKShippingSection(item, partCatalog, cardKey)}
                {renderCKMessagesSection(item, cardKey)}
            </div>
        );
    };

    return (
        <div className="w-full min-w-0 space-y-6">
            <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-bold text-foreground">{partName}</h2>
                    <Chip size="sm" color="primary" variant="flat" className="font-medium" radius="md">{preferredChipLabel}</Chip>
                </div>
                <p className="text-sm text-default-500">Part # {partNumber}</p>
                <div className="flex items-center gap-6 flex-wrap text-sm text-default-500 pb-3 border-b border-default-200">
                    {isForte ? (
                        <span className="inline-flex items-center gap-1.5">
                            <Icon icon="mdi:calendar-outline" className="w-4 h-4 text-default-500" />
                            {t('Last Part Update')}: {forteLastPartUpdate ?? '—'}
                        </span>
                    ) : (
                        <>
                            <span className="inline-flex items-center gap-1.5">
                                <Icon icon="mdi:calendar-outline" className="w-4 h-4 text-default-500" />
                                {t('Quote')}: {quoteDateStr}
                            </span>
                            {hasNonPowertrainItems && (
                                <span className="inline-flex items-center gap-1.5">
                                    <Icon icon="mdi:truck-delivery-outline" className="w-4 h-4 text-default-500" />
                                    {t('Delivery By')}: {deliveryDateStr}
                                </span>
                            )}
                        </>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-6 items-stretch">
                <div className="flex flex-col min-h-0 flex-1">
                    <div className="rounded-lg border border-default-200 overflow-hidden flex flex-col flex-1 min-h-[380px]">
                        <div className="p-2 px-1 shrink-0">
                            <div className="flex items-center gap-2 font-semibold text-default-700">
                                <Icon icon="mdi:image-outline" className="w-5 h-5 text-default-500" />
                                {t('Part Image')}
                            </div>
                        </div>
                        <div className="rounded-b-lg border-t border-default-200 bg-content2/50 p-4 flex-1 flex items-center justify-center min-h-[320px]">
                            {firstResourceUrl ? (
                                <div className="w-full h-full min-h-[320px] flex items-center justify-center bg-white rounded border border-default-200 overflow-hidden">
                                    <img src={firstResourceUrl.url} alt={firstResourceUrl?.description || t('Part Image')} className="max-w-full max-h-full w-auto h-auto object-contain" />
                                </div>
                            ) : (
                                <div className="w-full h-full min-h-[320px] flex items-center justify-center rounded border border-default-200 bg-default-100 text-sm text-default-500">
                                    {t('No Image Found')}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="rounded-lg border border-default-200 p-4 space-y-4 min-w-0">
                    <div className="flex items-center justify-between">
                        <h3 className="text-base font-bold text-foreground">{t('Pricing & Availability')}</h3>
                        <span className="text-xs text-default-500">{t('All options shown')}</span>
                    </div>
                    <div className="overflow-x-auto overflow-y-visible w-full min-w-0">
                        <div className="flex flex-row flex-nowrap gap-4 w-full min-w-0 items-stretch">
                            {catalogRowsForDisplay.map((rowItem: any, idx: number) => {
                                const pc = rowItem.partCatalog as string;
                                const isPt = isPowertrainCatalog(pc);
                                const isPreferredCard =
                                    (pc === 'OEMPart' && oemIsPreferred) || (pc === 'OEMAftermarket' && !oemIsPreferred && !!aftermarketItem);
                                const cardMinW = isPt ? 'min-w-[420px] flex-[1_0_420px]' : 'min-w-[280px] flex-[1_0_280px]';
                                return (
                                    <div
                                        key={`${pc}-${idx}`}
                                        className={`flex min-h-0 ${cardMinW}`}
                                    >
                                        {renderCKOptionCard({
                                            item: rowItem,
                                            title: getCkCatalogTitle(pc),
                                            partCatalog: pc,
                                            isPreferred: isPreferredCard,
                                            cardKey: `ck-${pc}-${idx}`,
                                            showAlternativeChipWhenEmpty: false,
                                            cardClassNameExtra: 'h-full w-full flex flex-col min-h-0',
                                            showDeliveryDate: isPt,
                                        })}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const fallbackArrayItemKey = (item: unknown, idx: number): string => {
    if (item && typeof item === 'object' && !Array.isArray(item)) {
        const record = item as Record<string, unknown>;
        const stableId = record.id ?? record.source_id ?? record.NHTSACampaignNumber;
        if (stableId != null) return `item-${String(stableId)}`;
        const keys = Object.keys(record).sort((a, b) => a.localeCompare(b));
        return `item-${idx}-${keys.slice(0, 3).join('-')}`;
    }
    return `item-${idx}-${String(item)}`;
};

/** Simple key/value fallback for non-CK/forte/teal data (used when caller does not pass renderFallback). */
const renderSimpleFallback = (data: unknown): React.ReactNode => {
    if (data == null) return <span className="renderer-field-value text-default-500">--</span>;
    if (Array.isArray(data)) {
        if (data.length === 0) return <span className="renderer-field-value text-default-500">--</span>;
        return (
            <div className="space-y-2">
                {data.map((item, idx) => (
                    <div key={fallbackArrayItemKey(item, idx)} className="rounded border border-default-200 p-2 bg-content2/40">
                        {renderSimpleFallback(item)}
                    </div>
                ))}
            </div>
        );
    }
    if (typeof data === 'object') {
        const entries = Object.entries(data as Record<string, unknown>);
        if (entries.length === 0) return <span className="renderer-field-value text-default-500">--</span>;
        return (
            <div className="space-y-2">
                {entries.map(([k, v]) => {
                    const label = formatSectionTitle(k);
                    const format = getFieldFormat(k);
                    if (format) {
                        const formattedField = renderClaimScoreFormattedField(k, label, format, v);
                        if (formattedField || format === 'links') return formattedField;
                    }
                    const isNestedObject = v != null && typeof v === 'object';
                    let valueContent: React.ReactNode;
                    if (isNestedObject) {
                        valueContent = renderSimpleFallback(v);
                    } else if (v == null || v === '') {
                        valueContent = <span>--</span>;
                    } else {
                        valueContent = <span>{typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean' ? String(v) : '--'}</span>;
                    }
                    return (
                        <div key={k} className="renderer-key-value-block">
                            <div className="renderer-field-name capitalize font-medium text-default-700 mb-1">
                                {label}
                            </div>
                            <div className="renderer-field-value text-default-500">
                                {valueContent}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }
    const displayText = typeof data === 'string' || typeof data === 'number' || typeof data === 'boolean'
        ? String(data)
        : '--';
    return <span className="renderer-field-value text-default-500">{displayText}</span>;
};

export type SourceJsonModalProps = {
    state: SourceJsonModalState | null;
    onClose: () => void;
    timestamp?: string;
    /** Optional renderer for non-CK/forte/teal data (legacy parity). When omitted, a simple JSON fallback is used. */
    renderFallback?: (title: string, data: unknown) => React.ReactNode;
};

export const SourceJsonModal: React.FC<SourceJsonModalProps> = ({ state, onClose, timestamp, renderFallback }) => {
    const { t } = useTranslation();
    return (
        <Modal
            isOpen={!!state}
            onOpenChange={(open) => !open && onClose()}
            size="3xl"
            hideCloseButton
            classNames={{
                base: 'max-w-[95vw] max-h-[100vh] w-full',
                body: 'p-3 sm:p-4',
                header: 'border-b border-default-200',
            }}
            scrollBehavior="inside"
        >
            <ModalContent>
                {(close) => (
                    <>
                        <ModalHeader className="flex flex-col gap-1">
                            <div className="flex items-center justify-between w-full">
                                <span className="renderer-field-title capitalize">
                                    {formatKeyForDisplay((state?.title ?? '').replace(/_source$/i, ''))}
                                </span>
                                <Button isIconOnly variant="light" size="sm" onClick={close} aria-label={t('Close')}>
                                    <Icon icon="mdi:close" className="w-5 h-5" />
                                </Button>
                            </div>
                        </ModalHeader>
                        <ModalBody className="p-3 sm:p-4 overflow-auto max-h-[85vh] min-w-0">
                            {state?.data != null && (() => {
                                const { title, data } = state;
                                const isCkSource = title === 'C_&_K_source' || title === 'C&K_source';
                                const isForteSource = title === 'forte_source';
                                const isTealSource = title === 'Teal_source';
                                if (isCkSource && Array.isArray(data) && data.length > 0) {
                                    return <PartSourceModalContent data={data} quoteTimestamp={timestamp} sourceType="ck" />;
                                }
                                if (isForteSource && data && typeof data === 'object') {
                                    const forteItems = normalizeForteToPartItems(data);
                                    if (forteItems.length > 0) {
                                        return <PartSourceModalContent data={forteItems} quoteTimestamp={timestamp} sourceType="forte" />;
                                    }
                                }
                                if (isTealSource && data && typeof data === 'object') {
                                    const tealItems = normalizeTealToPartItems(data);
                                    if (tealItems.length > 0) {
                                        return <PartSourceModalContent data={tealItems} quoteTimestamp={timestamp} sourceType="teal" />;
                                    }
                                }
                                return renderFallback ? renderFallback(title, data) : renderSimpleFallback(data);
                            })()}
                        </ModalBody>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
};

/** Map a row's source/cell value to one of the legacy modal title shapes (`C_&_K_source`, `forte_source`, `Teal_source`).
 *  For other sources, falls back to a generic `<value> Info` title. */
export function mapSourceValueToModalTitle(sourceValue: unknown, fallbackLabel?: string): string {
    if (sourceValue == null || typeof sourceValue === 'object') {
        return fallbackLabel ?? 'Info';
    }
    let raw = '';
    if (typeof sourceValue === 'string') {
        raw = sourceValue.trim();
    } else if (typeof sourceValue === 'number' || typeof sourceValue === 'boolean') {
        raw = String(sourceValue);
    }
    const lower = raw.toLowerCase();
    if (lower.includes('c&k') || lower.includes('c_&_k') || lower === 'ck') return 'C_&_K_source';
    if (lower.includes('forte')) return 'forte_source';
    if (lower.includes('teal')) return 'Teal_source';
    return raw;
}
